/**
 * shell-bridge.js — Detection + Wrapper fuer window.zapShell (Z5 embedded
 * mode, Kontrakt: platform-design/design/z5-spa-embedded-mode.md §2).
 *
 * Die Shell (zap-client-shell bzw. native WebViews auf iOS/Android)
 * injiziert `window.zapShell` VOR dem SPA-Load. Die SPA programmiert nur
 * gegen diesen Wrapper — niemals gegen Transport-Details (QWebChannel /
 * messageHandlers / addJavascriptInterface).
 */

const REQUIRED_API_VERSION = 1;

export function isEmbeddedShell() {
  const s = globalThis.zapShell;
  return !!s && typeof s.apiVersion === 'number'
      && s.apiVersion >= REQUIRED_API_VERSION;
}

/**
 * @returns {object|null} das rohe Bridge-Objekt (API v1) oder null.
 * Version zu alt → null + ein console.warn: die SPA faellt dann auf den
 * normalen Web-Player zurueck (Shell zeigt halt Browser-Video — degradiert,
 * aber funktional), statt gegen eine inkompatible API zu laufen.
 */
export function getShell() {
  const s = globalThis.zapShell;
  if (!s) return null;
  if (typeof s.apiVersion !== 'number' || s.apiVersion < REQUIRED_API_VERSION) {
    try {
      console.warn(`[zap-shell] apiVersion ${s?.apiVersion} < ${REQUIRED_API_VERSION} — embedded mode aus, Web-Player-Fallback`);
    } catch { /* noop */ }
    return null;
  }
  return s;
}

/**
 * Asynchrone Bridge-Beschaffung — der korrekte Einstieg fuer den Player
 * (Kontrakt §6/A1, Team D). Zwei Faelle, die `getShell()` allein nicht abdeckt:
 *   - Android-Alt-/AOSP-WebView hat KEINEN Document-Start-Hook → `window.zapShell`
 *     erscheint erst NACH dem ersten SPA-Script. Daher nicht allein auf synchrone
 *     Praesenz gaten, sondern auch auf das `zapshellready`-DOM-Event hoeren.
 *   - Qt-Transport (QWebChannel) wird async live: das Objekt ist da, aber Calls
 *     erst nach `await zapShell.ready` nutzbar.
 *
 * @param {{timeoutMs?: number}} [opts] timeoutMs<=0 → reiner Sync-Check ohne Warten
 * @returns {Promise<object|null>} versionsgeprueftes Bridge-Objekt oder null
 */
export function getShellWhenReady({ timeoutMs = 3000 } = {}) {
  return new Promise((resolve) => {
    const finish = async () => {
      const s = getShell();             // Versions-Gate erst hier (Objekt kann jetzt da sein)
      if (!s) return resolve(null);
      try { await s.ready; } catch { /* Transport-Ready ist best-effort; trotzdem weiter */ }
      resolve(s);
    };
    if (globalThis.zapShell) return void finish();   // sync da → Event-Pfad sparen
    if (timeoutMs <= 0) return resolve(null);

    let done = false;
    const cleanup = () => globalThis.removeEventListener?.('zapshellready', onReady);
    const onReady = () => { if (done) return; done = true; clearTimeout(timer); cleanup(); finish(); };
    const timer = setTimeout(() => {
      if (done) return;
      done = true; cleanup();
      // Spaet, aber doch da? (Objekt ohne Event aufgetaucht) → noch akzeptieren.
      resolve(globalThis.zapShell ? getShell() : null);
    }, timeoutMs);
    globalThis.addEventListener?.('zapshellready', onReady, { once: true });
  });
}
